import { useState } from "react";
import { CheckCircle2, XCircle, MessageSquarePlus } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { timecode } from "@/lib/format";

const LABELS = {
  dialogue_leakage: "Dialogue leakage in M&E", music_leakage: "Music leakage in dialogue/FX", missing_effects: "Missing effects",
  missing_ambience: "Missing ambience", musical_artifacts: "Musical artifacts", phase_problems: "Phase problems",
  clicks: "Clicks / pops", gaps: "Gaps / dropouts", processing_artifacts: "Processing artifacts",
};

export const QCPanel = ({ job, onUpdated, currentTime }) => {
  const [note, setNote] = useState("");
  const checklist = job.qc_checklist || {};

  const save = async (patch) => {
    try {
      await api.put(`/qc/${job.id}`, patch);
      onUpdated();
    } catch (e) { toast.error("Could not save QC"); }
  };
  const toggle = (k) => save({ checklist: { ...checklist, [k]: !checklist[k] } });
  const addNote = async () => {
    if (!note.trim()) return;
    await api.post(`/qc/${job.id}/notes`, { text: note.trim(), timecode: currentTime });
    setNote("");
    onUpdated();
  };
  const flagged = Object.values(checklist).filter(Boolean).length;

  return (
    <div data-testid="qc-panel" className="panel p-4 space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="font-display font-semibold">QC Review</h3>
        <span data-testid="qc-status-badge" className={`font-mono text-[11px] uppercase tracking-wider px-2 py-0.5 rounded-sm border ${job.qc_status === "pass" ? "text-emerald-400 border-emerald-700" : job.qc_status === "fail" ? "text-rose-400 border-rose-700" : "text-slate-400 border-slate-600"}`}>{job.qc_status}{job.qc_reviewed_by ? ` · ${job.qc_reviewed_by}` : ""}</span>
      </div>
      <div>
        <div className="micro mb-2">Issue checklist · {flagged} flagged</div>
        <div className="grid grid-cols-1 gap-1">
          {(job.qc_items || Object.keys(LABELS)).map((k) => (
            <label key={k} data-testid={`qc-checklist-item-${k.replace(/_/g, "-")}`} className="flex items-center gap-2 text-sm px-2 py-1.5 rounded-sm hover:bg-[#1E2738] cursor-pointer">
              <input type="checkbox" checked={!!checklist[k]} onChange={() => toggle(k)} className="accent-rose-500" />
              <span className={checklist[k] ? "text-rose-300" : "text-slate-300"}>{LABELS[k] || k}</span>
            </label>
          ))}
        </div>
      </div>
      <div className="flex gap-2">
        <button data-testid="qc-pass-button" onClick={() => save({ qc_status: "pass" })} className="flex-1 h-9 rounded-sm bg-emerald-500/15 border border-emerald-700 text-emerald-300 hover:bg-emerald-500/25 text-sm font-semibold flex items-center justify-center gap-2"><CheckCircle2 size={15} /> Pass</button>
        <button data-testid="qc-fail-button" onClick={() => save({ qc_status: "fail" })} className="flex-1 h-9 rounded-sm bg-rose-500/15 border border-rose-700 text-rose-300 hover:bg-rose-500/25 text-sm font-semibold flex items-center justify-center gap-2"><XCircle size={15} /> Fail</button>
      </div>
      <div>
        <div className="micro mb-1.5">Operator notes (stamped at {timecode(currentTime)})</div>
        <div className="flex gap-2">
          <input data-testid="qc-operator-notes-input" value={note} onChange={(e) => setNote(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addNote()} placeholder="e.g. music bleed under line at this TC"
            className="flex-1 bg-[#0B0E14] border border-[#253044] rounded-sm px-2.5 py-1.5 text-sm focus:outline-none focus:border-cyan-500" />
          <button data-testid="qc-add-note-button" onClick={addNote} className="h-8 w-9 grid place-items-center rounded-sm border border-[#3A4A68] hover:bg-[#1E2738]"><MessageSquarePlus size={15} /></button>
        </div>
        <div className="mt-2 space-y-1 max-h-40 overflow-y-auto">
          {(job.qc_notes || []).slice().reverse().map((n, i) => (
            <div key={i} data-testid="qc-note-item" className="text-xs px-2 py-1.5 rounded-sm bg-[#0B0E14] border border-[#1c2433]">
              <span className="font-mono text-emerald-400 mr-2">{timecode(n.timecode || 0)}</span><span className="text-slate-300">{n.text}</span>
              {n.by && <span className="text-slate-500 ml-2">— {n.by}</span>}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
